"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-storage-capacity-calculator",
  "freezer-meat-storage-calculator",
  "freezer-energy-cost-calculator",
  "food-storage-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Freezer Vacuum Sealer Calculator",
  category: "Calculator" as const,
  mediumDescription:
    "See how much extra freezer capacity you gain by vacuum sealing instead of bagging or boxing. Sealed flat packages save 30–40% of freezer volume and extend storage life 3–5x by preventing freezer burn.",
  faqs: [
    { question: "How much space does vacuum sealing actually save?", answer: "Roughly 30–40% of freezer volume, depending on what you're storing. Meat compresses well (40%), prepared meals less (30%), mixed contents around 35% on average." },
    { question: "Does vacuum sealing extend frozen food storage life?", answer: "Yes — vacuum-sealed food typically lasts 3–5x longer in the freezer because removing air prevents freezer burn (the dehydration that destroys quality)." },
    { question: "What should I never vacuum seal?", answer: "Soft cheeses (compress too much), fresh mushrooms (need to breathe), raw garlic and onions (botulism risk), soft breads (crush), and delicate berries (freeze first, then seal)." },
    { question: "How do I get maximum space savings?", answer: "Freeze liquids and ground meat in flat rectangular packages — they stack like books. Round containers waste freezer volume; flat-packed sealed bags don't." },
    { question: "How much does a home vacuum sealer cost?", answer: "Entry-level home vacuum sealers run $50–100; mid-range chamber-style or dual-piston models run $150–300. Bags cost $0.25–0.50 each. Most home users break even within 6–12 months from reduced food waste." },
    { question: "Should I vacuum seal everything in the freezer?", answer: "It pays off most for proteins, bulk meal prep, and anything you'll store more than 3 months. Short-term items in their original packaging are fine." },
  ],
  relatedTools: ["freezer-storage-capacity-calculator", "freezer-meat-storage-calculator", "freezer-energy-cost-calculator"],
}

export function VacuumSealerSpaceOptimizerPageClient() {
  const [freezerSize, setFreezerSize] = useState("15")
  const [itemType, setItemType] = useState("mixed")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // A freezer cabinet runs ~1 cu ft (compact personal) to ~100 (commercial chest).
    const size = readQuantity(freezerSize, { min: 1, max: 100 })
    if (size === null) return null

    let spaceSavingsPercent = 0
    if (itemType === "meat") spaceSavingsPercent = 40
    else if (itemType === "vegetables") spaceSavingsPercent = 35
    else if (itemType === "prepared-meals") spaceSavingsPercent = 30
    else spaceSavingsPercent = 35 // mixed

    const regularStorage = size
    const spaceSaved = size * (spaceSavingsPercent / 100)
    const vacuumSealed = size + spaceSaved
    const percentSaved = spaceSavingsPercent

    return {
      regularStorage,
      vacuumSealed: parseFloat(vacuumSealed.toFixed(1)),
      spaceSaved: parseFloat(spaceSaved.toFixed(1)),
      percentSaved,
    }
  }, [freezerSize, itemType])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="How much"
      heroTitleItalic="extra freezer"
      heroTitlePost="do vacuum bags get you?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~15 sec", "Industry vacuum-seal density data"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "40%", label: "Space saved", note: "Vacuum-sealed meat" },
            { value: "3–5x", label: "Storage life", note: "vs unsealed" },
            { value: "30%", label: "Prepared meals", note: "Compression" },
            { value: "$50+", label: "Home sealer", note: "Entry price" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "freezer-vacuum-sealer-calculator")}
      cta={{
        title: "Out of room even with vacuum bags?",
        body: "Browse chest freezers — the densest, most cost-effective bulk-storage solution.",
        buttonLabel: "Browse chest freezers",
        buttonHref: "/collections/chest-freezers",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Volume.
                <br />
                Contents type.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Meat compresses best (~40%), then vegetables (~35%), then prepared meals (~30%).
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Freezer size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step="0.1"
                      value={freezerSize}
                      onChange={(e) => setFreezerSize(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Primary contents
                  </label>
                  <select
                    value={itemType}
                    onChange={(e) => setItemType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="meat">Meat &amp; poultry (40% saved)</option>
                    <option value="vegetables">Vegetables &amp; fruit (35% saved)</option>
                    <option value="prepared-meals">Prepared meals (30% saved)</option>
                    <option value="mixed">Mixed contents (35% avg)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Skip vacuum sealing for fresh mushrooms and soft cheeses.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Effective capacity (sealed)
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.vacuumSealed.toFixed(1) ?? "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">cu ft</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `+${result.spaceSaved} cu ft of usable space — ${result.percentSaved}% gain over loose storage.` : "Enter freezer details to see space gain."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Original capacity</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.regularStorage ?? "—"} cu ft</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Space gained</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">+{result?.spaceSaved ?? "—"} cu ft</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Maximize bulk storage"
          title="Chest freezers built for vacuum-bag stacking"
          description="Flat sealed packages stack like books in a chest freezer — pack 40 lb per cu ft of dense food."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                Air out. <em className="italic text-[#ff9951]">Volume</em> back.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Compression rates are industry-standard estimates from vacuum-sealing manufacturer documentation and USDA freezer-burn / quality-loss research.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Compression by content", d: "Meat compresses ~40% (no air pockets in tissue), vegetables ~35%, prepared meals ~30%, mixed contents ~35%." },
                { n: "02", t: "Effective volume gain", d: "Effective capacity = original cu ft × (1 + compression %). A 15 cu ft freezer with sealed meat behaves like ~21 cu ft of loose storage." },
                { n: "03", t: "Quality life multiplier", d: "Removing air prevents oxidation and freezer burn (USDA), extending best-quality storage life by 3–5x for sealed packages." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Upright freezers — easy to stack sealed bags"
          description="Wire shelves and basket drawers make organizing flat vacuum-sealed packages effortless."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
